import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import {
  QaiCallFeedbackComponent,
  QaiGlyphComponent,
  QaiPostCallComponent,
} from '@qai/ui';
import { TranslocoService } from '@jsverse/transloco';
import { CallService } from 'src/app/services/learner/call.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AmplitudeTrackingEventPropValues } from 'src/app/utils/enums/amplitude/amplitudeTrackingEventPropValues.enum';
import { HotjarService } from 'src/app/services/external-tools/hotjar.service';
import { PathwayLocalStorageUtil } from 'src/app/utils/pathway-localstorage.util';

const SCOPE = 'results';

@Component({
  host: {
    class: 'theme-dark',
  },
  selector: 'app-roleplay-post-call',
  standalone: true,
  imports: [QaiPostCallComponent, QaiCallFeedbackComponent, QaiGlyphComponent],
  templateUrl: './roleplay-post-call.component.html',
  styleUrls: ['./roleplay-post-call.component.scss'],
})
export class RoleplayPostCallComponent implements OnInit, OnDestroy {
  private callService = inject(CallService);
  private translocoService = inject(TranslocoService);
  private logger = inject(LoggerService);
  private amplitudeService = inject(AmplitudeService);
  private hotjarService = inject(HotjarService);

  readonly feedbackSubmitted = signal<boolean>(false);
  readonly selectedFeedback = signal<boolean | null>(null);

  readonly state = input<'processing' | 'feedback_given' | 'delay' | 'error'>('processing');
  readonly practiceCallUuid = input<string>('');
  readonly callType = input<string>('');
  readonly moduleAssignmentUuid = input<string>('');
  readonly callUuid = input<string>('');
  readonly endedEarly = input<boolean>(false);
  readonly viewResults = output<void>();
  readonly returnToPathway = output<void>();

  isCompleted: boolean = true;
  title: string = '';

  readonly ctaLabel = this.translocoService.translate(
    'VIEW_RESULTS',
    {},
    SCOPE,
  );
  readonly thankYouMessagePositive = this.translocoService.translate(
    'FEEDBACK_THANK_YOU',
    {},
    SCOPE,
  );
  readonly feedbackImprovementSuffix = this.translocoService.translate(
    'FEEDBACK_IMPROVEMENT_SUFFIX',
    {},
    SCOPE,
  );
  readonly processingLabel = this.translocoService.translate(
    'ROLEPLAY_POST_CALL_PROCESSING_LABEL',
    {},
    SCOPE,
  );
  readonly feedbackQuestion = this.translocoService.translate(
    'ROLEPLAY_POST_CALL_FEEDBACK_QUESTION',
    {},
    SCOPE,
  );

  readonly titleCallEnded = this.translocoService.translate('CALL_ENDED_GENERIC', {}, 'call');
  readonly returnToPathwayLabel = this.translocoService.translate('RETURN_TO_PATHWAY', {}, 'learningPathway');
  readonly exitPathwayLabel = this.translocoService.translate('EXIT_PATHWAY', {}, 'learningPathway');
  readonly noLeftContent = computed(
    () => this.feedbackSubmitted() && this.selectedFeedback() === false,
  );

  // Clears the call UUID when results are ready or polling completes on the post-call screen,
  // preventing redundant polling on the overview page.
  readonly clearPostCallPending = effect(() => {
    const state = this.state();
    if (state === 'delay' || state === 'feedback_given') PathwayLocalStorageUtil.clearRoleplayPending();
  });

  readonly resolvedCtaLabel = computed(() => {
    if (this.endedEarly()) return this.returnToPathwayLabel;
    if (this.state() === 'delay') return this.exitPathwayLabel;
    return this.ctaLabel;
  });

  readonly subtitle = computed(() => {
    switch (this.state()) {
      case 'delay':
        return this.translocoService.translate('ROLEPLAY_POST_CALL_SUBTITLE_DELAY', {}, SCOPE);
      case 'error':
        return this.translocoService.translate('ROLEPLAY_POST_CALL_SUBTITLE_ERROR', {}, SCOPE);
      default:
        return this.translocoService.translate('ROLEPLAY_POST_CALL_SUBTITLE_PROCESSING', {}, SCOPE);
    }
  });

  ngOnInit(): void {
    if (!this.endedEarly() && this.practiceCallUuid()) {
      PathwayLocalStorageUtil.markRoleplayPending(this.practiceCallUuid());
    }

    this.isCompleted = !this.endedEarly();

    this.title = this.isCompleted
      ? this.translocoService.translate('ROLEPLAY_POST_CALL_TITLE', {}, SCOPE)
      : this.titleCallEnded;

    this.logger.log('[CALL] roleplay post-call - init', {
      endedEarly: this.endedEarly(),
      isCompleted: this.isCompleted,
      title: this.title,
    });
  }

  onCtaClick(): void {
    if (this.endedEarly() || this.state() === 'delay') {
      this.onReturnToPathway();
    } else {
      this.onViewResults();
    }
  }

  onViewResults(): void {
    this.viewResults.emit();
  }

  onReturnToPathway(): void {
    this.returnToPathway.emit();
  }

  submitFeedback(feedback: boolean): void {
    this.selectedFeedback.set(feedback);
    this.feedbackSubmitted.set(true);

    const practiceCallUuid = this.practiceCallUuid();
    const callType = this.callType();
    this.trackAmplitudeEvent(feedback, callType);

    this.logger.log('[CALL] roleplay post-call - click feedback', {
      isPositive: feedback,
    });

    this.onSubmitFeedback(feedback, practiceCallUuid);

    if (!feedback) {
      this.triggerHotjarSurvey(callType, practiceCallUuid);
    }
  }

  triggerHotjarSurvey(callType: string, practiceCallUuid: string): void {
    const accountUuid = localStorage.getItem('current_account_uuid') || '-';
    const attributes = {
      accountUuid,
      callType,
      practiceCallUuid,
      moduleAssignmentUuid: this.moduleAssignmentUuid(),
      callUuid: this.callUuid(),
      triggeredBy: 'RoleplayPostCallComponent',
    };
    this.hotjarService.openSurveyByEvent('practice_feedback_dislike', attributes);
    this.logger.log('[CALL] roleplay post-call - open survey', attributes);
  }

  trackAmplitudeEvent(feedback: boolean, callType: string): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_CLICK_FEEDBACK,
      {
        thumbs_type: feedback
          ? AmplitudeTrackingEventPropValues.FEEDBACK_THUMBS_UP
          : AmplitudeTrackingEventPropValues.FEEDBACK_THUMBS_DOWN,
        call_type: callType,
      },
    );
  }

  onSubmitFeedback(feedback: boolean, practiceCallUuid: string): void {
    this.callService.submitFeedback(practiceCallUuid, feedback).subscribe({
      next: () => {
        this.logger.log('[CALL] roleplay post-call - feedback submitted');
      },
      error: (err) => {
        this.logger.error(
          '[CALL] roleplay post-call - submit feedback error',
          err,
        );
      },
    });
  }

  ngOnDestroy(): void {
    console.log("RoleplayPostCallComponent destroyed, closing any open Hotjar survey");
    this.hotjarService.closeSurvey();
  }
}
